import React, { FC, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, FormGroup, Row } from 'reactstrap';
import { ExtendedUser } from '../../../../shared/user/extended';
import { UserNoteCreate, UserNoteSummary } from '../../../../shared/user/notes';
import { AddNoteForm } from '../../../modules/notes/addNoteForm';
import { UserNoteItem } from '../../../modules/notes/noteItem';
import { useTranslation } from '../../../translations';
import { Fetcher, useFetcher, useForm } from '../../../utils';

interface TabDetailsProps {
  readonly userData: ExtendedUser;
}

export const NotesTab: FC<TabDetailsProps> = ({ userData }) => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => {
    return await api.getNotes({ userId: userData.id });
  }, [userData.id]);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <Row>
      <Col lg={8} xs={12}>
        <Card className="mb-10" id={`notes${userData.id}`}>
          <CardHeader>{ts('common.notes')}</CardHeader>
          <NoteList notes={fetcher.data} refresh={fetcher.refresh} />
        </Card>
      </Col>
      <Col lg={4} xs={12}>
        <Card id="addNoteForm">
          <CardHeader>{ts('common.add_note')}</CardHeader>
          <CardBody>
            <AddNote refresh={fetcher.refresh} userId={userData.id} />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

interface NoteListProps {
  readonly notes: UserNoteSummary[];
  readonly refresh: () => void;
}

const NoteList: FC<NoteListProps> = ({ notes, refresh }) => {
  const { ts } = useTranslation();
  if (notes.length === 0) {
    return <CardBody className="text-secondary text-center">{ts('common.no_notes_user')}</CardBody>;
  }

  return notes.map((note) => {
    return <UserNoteItem key={note.id} note={note} refresh={refresh} />;
  });
};

const AddNote: FC<{ readonly userId: number; readonly refresh: () => void }> = ({
  userId,
  refresh,
}) => {
  const { ts } = useTranslation();
  const [note, setNote] = useState<UserNoteCreate>({
    category: 'misc',
    text: '',
    userId,
  });

  const form = useForm(async () => {
    await api.createUserNote(note);
    refresh();
  }, [note, refresh]);

  return (
    <form onSubmit={form.onSubmit}>
      <AddNoteForm hideUserInput note={note} setNote={setNote} />
      <FormGroup>
        <Button block color="primary" disabled={form.saving} id="addNewNote" type="submit">
          {ts('common.add_new_note')}
        </Button>
      </FormGroup>
    </form>
  );
};
